import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { faChevronDown, faSearch, faXmark } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Flex, Skeleton, Tag } from 'antd'
import styled from 'styled-components'
import { v4 as uuid } from 'uuid'

import EmptyState from 'src/components/EmptyState'
import Text, { TextSize } from 'src/components/Typography/Text'
import useDisclosure from 'src/hooks/use-disclosure'

import FormControl from '../FormControl/FormControl'
import InputBase from '../Input/InputBase'
import { useMenuKeyboardShortcuts } from '../Menu/use-menu-keyboard-shortcuts'
import Popover from '../Popover/Popover'

import MultiSelectItem from './MultiSelectItem'
import SelectMenuContent from './SelectMenuContent'
import { SelectBaseProps, SelectOption } from './types'
import { useSelectKeyboardShortcuts } from './use-select-keyboard-shortcuts'
import { useCreateOption, useFilteredOptions, useShowAsyncStartSearchingEmptyState } from './util'

const StyledLoadingState = styled(Flex).attrs({
  vertical: true,
  gap: '6px',
})`
  width: 100%;
  max-width: var(--radix-popper-anchor-width);
  padding: 6px;
  .ant-skeleton-input {
    height: 32px !important;
    width: 100% !important;
  }
`

const StyledTag = styled(Tag)`
  margin: 0;
  height: auto;
  background-color: ${({ theme }) => theme.colorBgContainerDisabled};
  border: solid 1px ${({ theme }) => theme.colorSplit};
  border-radius: 4px;
`
const StyledXButton = styled(Text).attrs({
  'aria-hidden': true,
  size: TextSize.Small,
})`
  outline: solid 1px transparent;
  border-radius: 4px;
  margin-left: 3px;
  padding: 0px 3px;
  cursor: pointer;
  align-self: center;
  &:focus {
    outline: solid 1px ${({ theme }) => theme.colorPrimaryActive};
  }
`

type SelectAllOption = {
  isGroupSelectAll?: boolean // If the option is a "Select all" option at the top of each group
  isUpdateAll?: boolean // If the option is the top level "Select all"/"Clear all" option.  Either select all if none/some items are selected, or clear all if all items are selected
  groupStartIndex?: number
  groupEndIndex?: number
}

type Props<TValue> = SelectBaseProps<TValue> & {
  selectedOptions: SelectOption<TValue>[] | undefined
  onSelectedOptionsChange: (selectedOptions: SelectOption<TValue>[]) => void | Promise<unknown>
  selectedOptionsDisplayText?: string
  isSelectAllEnabled?: boolean // Show the "Select all" option at the top of the dropdown and at the top of each group
}

const MultiSelect = <TValue,>({
  ariaLabel,
  caption,
  errorMessage,
  filterOption,
  getIsOptionDisabled,
  getOptionKey,
  isAsync = false,
  isClearable,
  isCreatable,
  isDisabled,
  isInvalid,
  isLoading,
  isRequired,
  itemLayout,
  label,
  onCreateOption,
  onSearchQueryChange,
  placeholder = 'Select',
  prefixIcon,
  onBlur: propOnBlur,
  onSelectedOptionsChange,
  options,
  selectedOptions,
  selectedOptionsDisplayText,
  size,
  suffixIcon,
  width,
  isSelectAllEnabled,
}: Props<TValue>) => {
  // This type allows for injecting information needed for the select all option
  type MultiSelectOption = SelectOption<TValue> & SelectAllOption

  const [id] = useState(uuid())
  const [menuId] = useState(uuid())
  const [searchQuery, setSearchQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const { isOpen: hasFocus, open: onFocus, close: onBlur } = useDisclosure()
  const inputRef = useRef<HTMLInputElement>(null)
  const { isOpen, open, close } = useDisclosure()

  const selectedOptionsByKey = useMemo(
    () =>
      new Map<React.Key, SelectOption<TValue>>(selectedOptions?.map((o) => [getOptionKey(o), o])),
    [getOptionKey, selectedOptions]
  )

  const filteredOptions = useFilteredOptions({
    isAsync,
    options,
    searchQuery,
    filterOption,
  })

  const isAllSelected = selectedOptions?.length === filteredOptions.length

  const isGroupAllSelected = useCallback(
    (startIndex: number, endIndex: number) => {
      const opts = filteredOptions.slice(startIndex, endIndex + 1) // endIndex is inclusive
      return opts.every((o) => selectedOptionsByKey.has(getOptionKey(o)))
    },
    [filteredOptions, getOptionKey, selectedOptionsByKey]
  )

  const displayedOptions: MultiSelectOption[] = useMemo(() => {
    if (!isSelectAllEnabled) {
      return filteredOptions.map((o) => ({ ...o, isSelectAll: false, isClearAll: false }))
    }

    const result: MultiSelectOption[] = []

    if (!searchQuery) {
      result.push({
        label: isAllSelected ? 'Clear all' : 'Select all',
        value: null as unknown as TValue, // This is a hack.  Value should never be used.
        isUpdateAll: true,
      })
    }

    let currentGroup: string | undefined
    for (let i = 0; i < filteredOptions.length; i++) {
      const o = filteredOptions[i]
      if (o) {
        if (isSelectAllEnabled && o?.group && o.group !== currentGroup) {
          // If isSelectAllEnabled and there is a new group, check to see if a select all option is needed
          let endIndex = filteredOptions.length - 1
          let activeCount = 0
          for (let j = i; j < filteredOptions.length; j++) {
            const o2 = filteredOptions[j]
            if (o2?.group !== o.group) {
              endIndex = j - 1
              break
            }
            if (!getIsOptionDisabled?.(o2)) {
              activeCount++
            }
          }

          if (activeCount >= 2) {
            // only add select all option if there are at least 2 active options in the group
            result.push({
              label: 'Select all',
              group: o.group,
              value: null as unknown as TValue, // This is a hack.  Value should never be used.
              isGroupSelectAll: true,
              groupStartIndex: i,
              groupEndIndex: endIndex,
            })
          }

          currentGroup = o.group
        }
        result.push({ ...o, isGroupSelectAll: false })
      }
    }

    return result
  }, [filteredOptions, getIsOptionDisabled, isAllSelected, isSelectAllEnabled, searchQuery])

  const handleBackSpace = useCallback(() => {
    if (searchQuery?.length > 0 || !selectedOptions?.length) {
      return
    }
    const newSelectedOptions = selectedOptions?.slice() ?? []
    newSelectedOptions.pop()
    void onSelectedOptionsChange(newSelectedOptions)
    inputRef.current?.focus()
  }, [onSelectedOptionsChange, selectedOptions, searchQuery?.length])

  const handleClose = useCallback(() => {
    close()
    setActiveIndex(0)
    setSearchQuery('')
  }, [close])

  const handleOpen = useCallback(() => {
    if (isDisabled) {
      return
    }
    inputRef.current?.focus()
    open()
  }, [isDisabled, open])

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      onBlur()
      propOnBlur?.(e)
    },
    [onBlur, propOnBlur]
  )

  const handleClear = useCallback(() => {
    void onSelectedOptionsChange([])
    onFocus()
  }, [onFocus, onSelectedOptionsChange])

  const handleValueChange = useCallback(
    (newValue: string) => {
      setSearchQuery(newValue)
      setActiveIndex(0)
      open()
    },
    [open]
  )

  useSelectKeyboardShortcuts({
    hasFocus,
    isOpen,
    onBackSpace: handleBackSpace,
    onClose: handleClose,
    onOpen: handleOpen,
  })

  const handleUpdateAll = useCallback(() => {
    if (isAllSelected) {
      void onSelectedOptionsChange([])
    } else {
      void onSelectedOptionsChange(filteredOptions)
    }
    onFocus()
  }, [filteredOptions, isAllSelected, onFocus, onSelectedOptionsChange])

  const handleOptionToggled = useCallback(
    (option: MultiSelectOption) => {
      if (option.isUpdateAll) {
        handleUpdateAll()
        return
      }

      if (option.isGroupSelectAll && option.group) {
        const groupOpts = options.filter(
          (o) => o.group === option.group && !getIsOptionDisabled?.(o)
        )
        const allSelected = groupOpts.every((o) => selectedOptionsByKey.has(getOptionKey(o)))
        let newSelectedOptions = selectedOptions?.slice() ?? []
        if (allSelected) {
          const toRemove = new Set(groupOpts.map((o) => getOptionKey(o)))
          newSelectedOptions = newSelectedOptions.filter((o) => !toRemove.has(getOptionKey(o)))
        } else {
          const map = new Map(newSelectedOptions.map((o) => [getOptionKey(o), o]))
          groupOpts.forEach((o) => {
            if (!map.has(getOptionKey(o))) {
              map.set(getOptionKey(o), o)
            }
          })
          newSelectedOptions = Array.from(map.values())
        }
        setSearchQuery('')
        void onSelectedOptionsChange(newSelectedOptions)
        inputRef.current?.focus()
        return
      }
      if (getIsOptionDisabled?.(option)) {
        return
      }
      const toggledKey = getOptionKey(option)
      let newSelectedOptions = selectedOptions?.slice() ?? []
      if (selectedOptionsByKey.has(toggledKey)) {
        newSelectedOptions = newSelectedOptions.filter((o) => getOptionKey(o) !== toggledKey)
      } else {
        newSelectedOptions.push(option)
      }
      setSearchQuery('')
      void onSelectedOptionsChange(newSelectedOptions)
      inputRef.current?.focus()
    },
    [
      getIsOptionDisabled,
      getOptionKey,
      handleUpdateAll,
      onSelectedOptionsChange,
      options,
      selectedOptions,
      selectedOptionsByKey,
    ]
  )

  const handleCreateOptionClicked = useCallback(() => {
    if (!searchQuery) {
      return
    }
    onCreateOption?.(searchQuery)
    setSearchQuery('')
    setActiveIndex(0)
  }, [onCreateOption, searchQuery, setSearchQuery])

  const activeOption = displayedOptions[activeIndex]
  const actionCount = displayedOptions.length + (isCreatable ? 1 : 0)
  const createActionIndex = isCreatable ? actionCount - 1 : -1
  const { menuItemRefs } = useMenuKeyboardShortcuts({
    activeIndex,
    count: actionCount,
    getIsItemDisabled: (index: number) => {
      if (index === createActionIndex) {
        return false
      }
      const option = displayedOptions[index]
      if (!option) {
        return true
      }
      if (option.isGroupSelectAll || option.isUpdateAll) {
        return false
      }
      return !!getIsOptionDisabled?.(option)
    },
    isEnabled: isOpen,
    onEscape: handleClose,
    onSelect: () => {
      if (activeIndex === createActionIndex) {
        handleCreateOptionClicked()
      }
      if (!activeOption) {
        return
      }
      handleOptionToggled(activeOption)
    },
    setActiveIndex,
  })

  useEffect(() => {
    // Reset active index when options change
    setActiveIndex(0)
  }, [displayedOptions])

  useEffect(() => {
    // Report changes in value
    onSearchQueryChange?.(searchQuery ?? '')
  }, [onSearchQueryChange, searchQuery])

  const getOptionId = useCallback(
    (option: MultiSelectOption) => {
      let suffix: string
      if (option.isUpdateAll) {
        suffix = 'update-all'
      } else if (option.isGroupSelectAll) {
        suffix = `select-all-${option.group}`
      } else {
        suffix = `${getOptionKey(option)}`
      }
      return `${id}-${suffix}`
    },
    [getOptionKey, id]
  )

  const createOption = useCreateOption({
    activeIndex,
    createActionIndex,
    isCreatable,
    onCreateOption: handleCreateOptionClicked,
    searchQuery,
    setActiveIndex,
  })

  const handleItemMouseOver = useCallback((index: number) => {
    setActiveIndex(index)
  }, [])

  const handleItemClick = useCallback(
    (option: MultiSelectOption) => {
      handleOptionToggled(option)
    },
    [handleOptionToggled]
  )

  const renderedOptions = useMemo(
    () => [
      ...displayedOptions.map((o, index) => {
        let key: string
        let isSelected: boolean
        let disabled: boolean

        if (o.isUpdateAll) {
          key = 'update-all'
          isSelected = false // Always un-selected
          disabled = false
        } else if (o.isGroupSelectAll) {
          key = `select-all-${index}`
          isSelected = isGroupAllSelected(o.groupStartIndex ?? 0, o.groupEndIndex ?? 0)
          disabled = false
        } else {
          key = `${getOptionKey(o)}`
          isSelected = selectedOptionsByKey.has(getOptionKey(o))
          disabled = getIsOptionDisabled?.(o) ?? false
        }

        return (
          <MultiSelectItem<TValue>
            key={`${o.group}-${key}`}
            id={getOptionId(o)}
            index={index}
            isActive={index === activeIndex}
            isDisabled={disabled}
            isSelected={isSelected}
            onMouseOver={handleItemMouseOver}
            onClick={handleItemClick}
            option={o}
            options={displayedOptions}
            menuItemRefs={menuItemRefs}
            isHideCheckbox={o.isUpdateAll}
          />
        )
      }),
      ...(createOption ? [createOption] : []),
    ],
    [
      activeIndex,
      createOption,
      displayedOptions,
      getIsOptionDisabled,
      getOptionId,
      getOptionKey,
      handleItemClick,
      handleItemMouseOver,
      isGroupAllSelected,
      menuItemRefs,
      selectedOptionsByKey,
    ]
  )

  const showAsyncStartSearchingEmptyState = useShowAsyncStartSearchingEmptyState({
    isAsync,
    isLoading,
    searchQuery,
  })

  return (
    <FormControl
      caption={caption}
      errorMessage={errorMessage}
      id={id}
      isInvalid={isInvalid}
      isRequired={isRequired}
      label={label}
      width={width}
    >
      {({ ariaDescribedBy, ariaInvalid, ariaLive }) => (
        <Popover isOpen={isOpen} onOpen={handleOpen} onClose={handleClose}>
          <Popover.Trigger asChild>
            <div style={{ width: '100%' }}>
              <InputBase
                ariaActiveDescendant={activeOption ? getOptionId(activeOption) : undefined}
                ariaControls={menuId}
                ariaDescribedBy={ariaDescribedBy}
                ariaExpanded={isOpen}
                ariaInvalid={ariaInvalid}
                ariaLabel={ariaLabel}
                ariaLive={ariaLive}
                id={id}
                isClearable={isClearable}
                isDisabled={isDisabled}
                isInvalid={isInvalid}
                isLoading={isLoading}
                isRequired={isRequired}
                onClear={handleClear}
                onBlur={handleBlur}
                onFocus={onFocus}
                onValueChange={handleValueChange}
                placeholder={!selectedOptions?.length ? placeholder : undefined}
                prefixIcon={prefixIcon}
                prefix={
                  <>
                    {selectedOptionsDisplayText ? (
                      <StyledTag>
                        <Flex gap="3px">
                          <Text whiteSpace="wrap">{selectedOptionsDisplayText}</Text>
                          <StyledXButton
                            onClick={(e) => {
                              handleUpdateAll()
                              e.preventDefault() // Prevent deleting from closing the dropdown by stealing focus
                              handleOpen()
                            }}
                          >
                            <FontAwesomeIcon icon={faXmark} />
                          </StyledXButton>
                        </Flex>
                      </StyledTag>
                    ) : (
                      selectedOptions?.map((o) => (
                        <StyledTag key={getOptionKey(o)}>
                          <Flex gap="3px">
                            <Text whiteSpace="wrap">{o.label}</Text>
                            <StyledXButton
                              onClick={(e) => {
                                handleOptionToggled(o)
                                e.preventDefault() // Prevent deleting from closing the dropdown by stealing focus
                                handleOpen()
                              }}
                            >
                              <FontAwesomeIcon icon={faXmark} />
                            </StyledXButton>
                          </Flex>
                        </StyledTag>
                      ))
                    )}
                  </>
                }
                ref={inputRef}
                role="combobox"
                size={size}
                suffixIcon={suffixIcon === null ? undefined : suffixIcon || faChevronDown}
                value={searchQuery}
              />
            </div>
          </Popover.Trigger>
          <SelectMenuContent id={menuId} aria-multiselectable={true} $layout={itemLayout}>
            {renderedOptions}
            {!renderedOptions.length && !isLoading && (
              <EmptyState hideBorder size="sm">
                <EmptyState.Image icon={showAsyncStartSearchingEmptyState ? faSearch : undefined} />
                <EmptyState.Message>
                  {showAsyncStartSearchingEmptyState
                    ? 'Type to start searching'
                    : 'No options found'}
                </EmptyState.Message>
              </EmptyState>
            )}
            {!filteredOptions.length && isLoading && (
              <StyledLoadingState>
                <Skeleton.Input active />
                <Skeleton.Input active />
                <Skeleton.Input active />
              </StyledLoadingState>
            )}
          </SelectMenuContent>
        </Popover>
      )}
    </FormControl>
  )
}

export default React.memo(MultiSelect) as typeof MultiSelect
